import { useRef, useContext, useMemo } from 'react'
import { GoogleMap, Marker, Polygon, Polyline, OverlayViewF } from '@react-google-maps/api'
import { routingColors } from 'constants/mockup'
import { EMapType, ICoordinates, IFleetRouteResult } from 'interface/map'
import { AppContext } from 'App'
import { DefaultAvatar } from 'icons'
import VehicleAvatar from 'assets/image/aptive-sp-vehicle.png'
import { RouteLayer } from 'interface/route'
import { decode } from '@mapbox/polyline'
import styles from './index.module.scss'

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '0.5rem',
}

export type MapProps = {
  mapType: EMapType
  mapData: IFleetRouteResult
  routeLayers: RouteLayer[]
  center: ICoordinates | undefined
  zoom: number
}

const Map: React.FC<MapProps> = (function ({ mapType, mapData, routeLayers, center, zoom }) {
  const { isGoogleMapAPILoaded, googleMapAPILoadError } = useContext(AppContext)
  const mapRef = useRef<google.maps.Map>()
  const activePolygonIndex: any = useRef()

  const handleLoadError = (error: Error | undefined) => {
    console.error('Error loading Google Maps:', error)
  }

  const onLoadMap = (map: google.maps.Map) => {
    mapRef.current = map
  }

  const onClickPolygon = (index: number) => {
    activePolygonIndex.current = index
  }

  const generatePolygonOption = (routeIndex: number) => {
    const polygonOption = {
      fillOpacity: 0.24,
      fillColor: routingColors[routeIndex % routingColors.length],
      strokeColor: routingColors[routeIndex % routingColors.length],
      strokeWeight: 2,
    }
    return polygonOption
  }

  const generateCircleSymbol = (routeIndex: number) => {
    const circleSymbol = {
      path: 'M-2.3 0a2.3 2.3 0 1 0 4.6 0a2.3 2.3 0 1 0 -4.6 0',
      strokeColor: '#000',
      fillColor: routingColors[routeIndex % routingColors.length],
      fillOpacity: 1,
      strokeWidth: 1,
      strokeOpacity: 1,
      scale: 2,
    }
    return circleSymbol
  }

  const generateLineSymbol = (routeIndex: number) => {
    return {
      path: 'M 0,-0.5 0,0.5',
      strokeOpacity: 1,
      scale: 2.2,
      strokeColor: routingColors[routeIndex % routingColors.length],
    }
  }

  const generateVehicle = () => {
    return (
      <div className={`${styles.vehicle} flex items-center justify-center`}>
        <img src={VehicleAvatar} alt="VehicleAvatar" />
      </div>
    )
  }

  const generateAvatar = (routeIndex: number, avatar?: string) => {
    const avatarBorderStyle = {
      border: `2px solid ${routingColors[routeIndex % routingColors.length]}`,
    }
    return (
      <>
        {avatar ? (
          <img className={`${styles.avatar}`} src={`data:image/png;base64,${avatar}`} alt="" style={avatarBorderStyle} />
        ) : (
          <div className={`${styles.avatar} flex items-center justify-center bg-white`} style={avatarBorderStyle}>
            <DefaultAvatar />
          </div>
        )}
      </>
    )
  }

  const routes = useMemo(() => {
    return mapData?.routes?.map((route, index) => ({
      polygon: {
        area: route.area,
        options: generatePolygonOption(index),
      },
      polyline: {
        path: (route.geometry ? decode(route.geometry) : []).map(([lat, lng]) => ({ lat, lng })),
        options: {
          strokeOpacity: 0,
          icons: [
            {
              icon: generateLineSymbol(index),
              offset: '0',
              repeat: '6px',
            },
          ],
          map: mapRef.current,
          visible: true
        }
      },
      homes: route.appointments.map((appointment) => ({
        title: appointment.id.toString(),
        icon: generateCircleSymbol(index),
        position: { lat: appointment.lat, lng: appointment.lng },
      })),
      vehicle: {
        position: route.tracking_data?.vehicle_location ? {
          lat: route.tracking_data.vehicle_location.lat,
          lng: route.tracking_data.vehicle_location.lng,
        } : null,
        children: generateVehicle()
      },
      avatar: {
        position: route.avatar_placement ? {
          lat: route.avatar_placement.lat,
          lng: route.avatar_placement.lng,
        } : null,
        children: generateAvatar(index, route.avatar),
        icon: `data:image/png;base64,${route.avatar}`
      }
    })) ?? []
  }, [mapData])

  const mapTypeId = useMemo(() => {
    if (mapType === EMapType.SATELLITE) {
      return 'satellite'
    }
    if (mapType === EMapType.TERRAIN) {
      return 'terrain'
    }
    if (mapType === EMapType.TRAFFIC) {
      return 'roadmap'
    }
    return 'roadmap'
  }, [mapType])

  if (googleMapAPILoadError) {
    handleLoadError(googleMapAPILoadError)
    return <div>Error loading Google Maps!</div>
  }

  return (
    isGoogleMapAPILoaded && mapData ?
      <div className='relative h-full'>
        {routes.length > 0 ?
          <GoogleMap
            zoom={zoom}
            center={center}
            onLoad={onLoadMap}
            mapContainerStyle={containerStyle}
            mapTypeId={mapTypeId}
          >
            {routes.map((route, index: number) => (
              <div key={index}>
                <Polyline
                  visible={routeLayers.includes('routes')}
                  path={route.polyline.path}
                  options={route.polyline.options}
                />
                <Polygon
                  visible={routeLayers.includes('route-polygon')}
                  onMouseDown={() => onClickPolygon(index)}
                  options={route.polygon.options}
                  paths={route.polygon.area}
                />
                {route.homes.map((home, homeIndex) => (
                  <Marker
                    visible={routeLayers.includes('homes')}
                    zIndex={1}
                    key={homeIndex}
                    icon={home.icon}
                    title={home.title}
                    position={home.position}
                  />
                ))}
                {routeLayers.includes('service-vehicles') && route.vehicle.position && (
                  <OverlayViewF
                    zIndex={1}
                    position={route.vehicle.position}
                    mapPaneName={'floatPane'}
                    children={route.vehicle.children}
                  />
                )}
                {routeLayers.includes('service-pro') && route.avatar.position && (
                  <OverlayViewF
                    zIndex={2}
                    position={route.avatar.position}
                    mapPaneName="markerLayer"
                  >
                    {route.avatar.children}
                  </OverlayViewF>
                )}
              </div>
            ))}
          </GoogleMap>
          :
          <>
            <div className='absolate text-center hidden'>There is no Polygon data...</div>
            <GoogleMap
              zoom={zoom}
              center={center}
              mapContainerStyle={containerStyle}
            >
            </GoogleMap>
          </>
        }
      </div >
      : <div>Loading Google Maps...</div>
  )
})

export default Map
